/**
 * ChatterWorkspace.tsx — thin chatter-mode wrapper around @crm/chat's ChatWorkspace (Phase 12-02).
 *
 * The full three-column workspace (tab strip, conversation list, thread, fan panel) was
 * relocated VERBATIM into @crm/chat/ChatWorkspace so the admin all-chats surface (Phase
 * 12-03) can reuse the identical implementation. This wrapper wires the injected slots —
 * BroadcastsModal (mass messages), TransactionsPane (Notifications) + its batch tx-unread map
 * + mark-seen callback, and FanPanel's "Recent payments" data source. The components + their
 * API clients (broadcastsApi/transactionsApi) were themselves relocated into @crm/chat
 * (2026-07-20) so the admin full-parity surface reuses them; transactionsApi is re-exported
 * through the chatter app's lib/api-client so this file's import path is unchanged.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChatWorkspace, BroadcastsModal, TransactionsPane } from '@crm/chat';
import { transactionsApi } from '../lib/api-client.js';

export function ChatterWorkspace() {
  const queryClient = useQueryClient();

  // Batch transactions unread-counts map (D-07-15). One round-trip covers ALL assigned
  // creators' tx unread counts — drives the amber badge on EVERY creator tab.
  const txUnreadQuery = useQuery({
    queryKey: ['chatter-tx-unread-counts'],
    queryFn: () => transactionsApi.unreadCounts(),
    staleTime: 60_000,
  });

  return (
    <ChatWorkspace
      // Phase 13 (D-13-01/02/04/05): chatters get the same All-tab machinery as admin
      // (allAccountsMode) but explicitly decoupled from admin's other three defaults —
      // full write parity (readOnly=false), ACTIVE passive-on-open (suppressPassiveOnOpen=
      // false, D-13-05), first-creator default landing (defaultToAllTab=false, D-13-02),
      // and preserved per-creator on-open refresh (disablePerCreatorRefresh=false; only the
      // All tab itself skips it via isAllTab in ChatWorkspace, D-13-04). basePath omitted —
      // defaults to /inbox, the chatter surface's existing mount point.
      allAccountsMode
      readOnly={false}
      suppressPassiveOnOpen={false}
      defaultToAllTab={false}
      disablePerCreatorRefresh={false}
      BroadcastsModal={BroadcastsModal}
      TransactionsPane={TransactionsPane}
      txUnreadMap={txUnreadQuery.data}
      onTransactionsViewOpen={(creatorId) => {
        transactionsApi
          .markSeen(creatorId)
          .then(() => queryClient.invalidateQueries({ queryKey: ['chatter-tx-unread-counts'] }))
          .catch(() => {
            /* non-fatal: badge may not clear immediately */
          });
      }}
      // D-13-03/07: fires once when the merged Notifications view opens on the All tab —
      // marks EVERY assigned creator seen in one call so all per-tab badges clear.
      onAllTransactionsViewOpen={() => {
        transactionsApi
          .markSeenAll()
          .then(() => queryClient.invalidateQueries({ queryKey: ['chatter-tx-unread-counts'] }))
          .catch(() => {
            /* non-fatal: badge may not clear immediately */
          });
      }}
      fetchRecentPayments={(memberId, creatorId) =>
        transactionsApi.chatterFeed(creatorId, undefined, undefined, memberId, 5)
      }
    />
  );
}
